import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../utils/hooks";
import VideoCall from "./VideoCall";
import ReturnToCallBar from "./ReturnToCallBar";
import {
  finalizeCallTeardown,
  isCallTearingDown,
  setLeaveCallNavigator,
} from "../../../utils/endCallSession";

/**
 * Keeps VideoCall mounted while the session is active so media continues
 * when the user navigates to chat. Fullscreen on /video-call; minimized elsewhere.
 * Return strip lives in Home (above chat header); floating fallback only outside Home.
 */
export default function ActiveCallShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const peerCallId = useAppSelector((s) => s.PeerJsSlice.call_id);
  const callJoined = useAppSelector((s) => s.PeerJsSlice.call_joined);
  const ssCallId =
    typeof window !== "undefined"
      ? Number(sessionStorage.getItem("call_id") || 0)
      : 0;
  // Gate LiveKit/Peer mount: incoming ring must not auto-join before Accept
  const callActive = !!(peerCallId || ssCallId) && callJoined;
  const onCallRoute =
    location.pathname === "/video-call" ||
    location.pathname.endsWith("/video-call");
  // Home layout hosts the embedded strip for authenticated app routes
  const onHomeShell =
    !onCallRoute &&
    !location.pathname.startsWith("/login") &&
    !location.pathname.startsWith("/otp") &&
    location.pathname !== "/user-details" &&
    location.pathname !== "/select-profile";

  useEffect(() => {
    setLeaveCallNavigator(() => {
      navigate("/chat", { replace: true });
      finalizeCallTeardown();
    });
    return () => setLeaveCallNavigator(null);
  }, [navigate]);

  const tearingDown = isCallTearingDown();
  const keepMounted = callActive || (onCallRoute && !tearingDown);
  if (!keepMounted) return null;

  return (
    <>
      <div
        className={
          onCallRoute
            ? "fixed inset-0 z-[90]"
            : "pointer-events-none invisible fixed inset-0 z-[90]"
        }
        aria-hidden={!onCallRoute}
      >
        <VideoCall />
      </div>
      {callActive && !onCallRoute && !onHomeShell ? (
        <ReturnToCallBar variant="floating" />
      ) : null}
    </>
  );
}
